import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GiftIcon } from 'lucide-react';
import { useCartActions, type CartState } from '../../contexts/StoreContext';
import { inputCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { euro } from '../../core/format/currency';
type Props = {
  gifts: CartState['gifts'];
  compact?: boolean;
};

/**
 * Vrijgespeelde cadeaus als eigen regels in de mand, onder de producten.
 *
 * Bewust géén nepproducten in de mand: ze tellen niet mee in aantal of bedrag, gaan niet als
 * bestelregel naar de server (die past de cadeauregels zelf toe) en verdwijnen vanzelf als de
 * mand onder de grens zakt. Ze zien er wel uit als een regel, zodat de klant ziet wat er in de
 * doos komt, en hier kiest ze ook welk reisformaat.
 */
export function CartGifts({
  gifts,
  compact = false
}: Props) {
  const {
    chooseGift
  } = useCartActions();
  const reduce = useReducedMotion();
  if (gifts.length === 0) return null;
  return <ul aria-label="Cadeaus bij je bestelling" className="divide-y divide-line border-t border-line" data-id="element-6901" data-mp="1bb60rm38pxqb">
      <AnimatePresence initial={false} data-id="element-6902" data-mp="1g9pmpmhu39io">
        {gifts.map(({
        tier,
        choice
      }) => {
        const selectId = `gift-${tier.ruleId}`;
        return <motion.li key={tier.ruleId} initial={reduce ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          transition: {
            duration: 0.15,
            ease
          }
        }} transition={{
          duration: 0.22,
          ease
        }} className={compact ? 'py-3' : 'py-4'} data-id="element-6903" data-mp="11ejgoziqnapc">
              {/* Kleiner dan een productregel: het product blijft de hoofdrol spelen. */}
              <div className="flex gap-4" data-id="element-6904" data-mp="1p9mazn0ba6d7">
                <span aria-hidden="true" className={`flex shrink-0 items-center justify-center bg-blush text-clay ${compact ? 'h-12 w-12' : 'h-14 w-14'}`} data-id="element-6905" data-mp="1p8b43ebkkihv">
                  <GiftIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-6906" data-mp="2rmyq6waq0dgp" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-6907" data-mp="2rkkxgu3ijdcb">
                  <div className="flex items-start justify-between gap-3" data-id="element-6908" data-mp="2moxa8k8lcu9g">
                    <div className="min-w-0" data-id="element-6909" data-mp="2ilr55fx7t94k">
                      {/* Ik-vorm, en nooit "gratis cadeau" (docs/VORMGEVING.md › Woorden). */}
                      <p className="text-[13px] text-clay" data-id="element-6910" data-mp="2dmnxrotriqtf">Cadeau bij je bestelling</p>
                      <h3 className={`mt-0.5 font-display leading-snug text-ink ${compact ? 'text-[15px]' : 'text-[1.05rem]'}`} data-id="element-6911" data-mp="28outx2fxm8wb">{tier.title}</h3>
                    </div>
                    <span className="shrink-0 text-[14px] tabular-nums text-clay" data-id="element-6912" data-mp="1yxrv67ah6607">{euro(0)}</span>
                  </div>
                  {!compact && !tier.choices?.length && <p className="mt-1 text-[13px] leading-snug text-muted" data-id="element-6913" data-mp="1uulh4dh4faj6">{tier.description}</p>}
                  {choice !== null && tier.choices && <div className="mt-2" data-id="element-6914" data-mp="1kzjp99vgnucw">
                      <label htmlFor={selectId} className="mb-1 block text-[13px] text-muted" data-id="element-6915" data-mp="1l0lcecpr47az">
                        Welke wil je?
                      </label>
                      {/* Native keuzelijst: op de telefoon de eigen kiezer van het toestel. */}
                      <select id={selectId} value={choice} onChange={e => chooseGift(tier.ruleId, e.target.value)} className={`${inputCls} py-2`} data-id="element-6916" data-mp="afowtj52zbk6">
                        {tier.choices.map(c => <option key={c} value={c} data-id="element-6917" data-mp="3dwnn4az47w73">
                            {c}
                          </option>)}
                      </select>
                    </div>}
                </div>
              </div>
            </motion.li>;
      })}
      </AnimatePresence>
    </ul>;
}